import { computeTaxes, round2 } from './tax-table.js';
// Returns the commitment discount rate for a (family, range, months) triple.
// LOCKED business rules — do not change without partner-sales team sign-off.
export function getCommitmentRate(family, range, months) {
    if (months === 0)
        return 0;
    if (family === 'baremetal') {
        if (range === 'advance') {
            if (months === 12)
                return 0.05;
            if (months === 24)
                return 0.10;
            if (months === 36)
                return 0.15;
        }
        if (range === 'scale' || range === 'high-grade') {
            if (months === 12)
                return 0.10;
            if (months === 24)
                return 0.15;
            if (months === 36)
                return 0.20;
        }
        return 0; // rise / kimsufi / soyoustart / game — no commitment discount
    }
    if (family === 'hpc_vmware') {
        if (months === 12)
            return 0.05;
        if (months === 24)
            return 0.10;
        if (months === 36)
            return 0.15;
    }
    return 0; // custom
}
export function getPartnerRates(tier) {
    switch (tier) {
        case 'standard': return { dealReg: 0.05, profitInc: 0 };
        case 'advanced': return { dealReg: 0.05, profitInc: 0.05 };
        case 'elite': return { dealReg: 0.05, profitInc: 0.10 };
        case 'none':
        default: return { dealReg: 0, profitInc: 0 };
    }
}
export function isPartnerDiscountEligible(line) {
    // Custom Lines: explicit per-line toggle takes precedence
    if (line.isCustomLine) {
        return line.applyPartnerDiscount === true;
    }
    if (line.isEco)
        return false;
    if (line.family === 'custom')
        return !!line.applyPartnerDiscountToCustom;
    return true;
}
// Business Support monthly floor, per market. Business support is 10% of the
// monthly subtotal but never less than this amount, in the market's own currency:
// CA = $400 CAD, WORLD (US dollars catalog) = $300 USD.
export const BUSINESS_SUPPORT_MINIMUM = {
    CA: 400,
    WORLD: 300,
};
// Business Support surcharge — applied to the partner-discounted monthly subtotal.
// CRITICAL: partner discount NEVER applies to this; support is computed AFTER and stays full price.
// `market` is optional and defaults to 'CA', so existing CA call sites are unchanged.
export function computeSupportSurcharge(monthly, tier, market = 'CA') {
    if (tier === 'standard')
        return 0;
    if (tier === 'business_baremetal')
        return round2(monthly * 0.05); // 5%, NO minimum
    if (tier === 'business') {
        // 10%, floored at the market minimum ($400 CAD / $300 USD)
        return Math.max(round2(monthly * 0.10), BUSINESS_SUPPORT_MINIMUM[market]);
    }
    return 0;
}
// Compute one line's monthly figures.
//   Step 1 — apply commitment discount to server price; apply to options whose
//            appliesCommitmentDiscount === true.
//   Step 2 — multiply by quantity.
//   Step 3 — apply partner discount (multiplicatively, eligibility-gated).
//   Step 4 — track each rounded breakdown amount in LineComputed.
//   Setup fee: 0 when commitMonths > 0; else basePriceSetup × qty.
/**
 * Clamp a custom discount to a sane percentage.
 *
 * Anything outside 0-100 — a typo, a stale value, a hand-edited saved deal —
 * is treated as no discount rather than trusted. A negative rate would raise
 * the price and one above 100 would make it negative; neither belongs on a
 * customer-facing quote.
 */
export function customDiscountRate(opt) {
    const pct = opt.customDiscountPct;
    if (typeof pct !== 'number' || !Number.isFinite(pct) || pct <= 0)
        return 0;
    return Math.min(pct, 100) / 100;
}
/** An option's price after its own custom discount, before any other. */
export function optionNetPrice(opt) {
    return round2(opt.monthlyPrice * (1 - customDiscountRate(opt)));
}
export function computeLine(line, partnerTier) {
    const commitRate = line.isCustomLine
        ? 0 // Custom Lines never get commitment discount
        : getCommitmentRate(line.family, line.range, line.commitMonths);
    // Server portion after commitment discount, per unit
    const serverAfterCommit = line.basePriceMonthly * (1 - commitRate);
    // Options: some get the commitment discount, some (RAM/disk) don't.
    // A presales-authored custom discount applies FIRST, to that option alone, so
    // the commitment rate then works on the already-reduced price — the same order
    // the rest of the cascade uses, and the reason each option can carry its own
    // rate without affecting any other line.
    let optionsTotal = 0;
    for (const opt of line.options) {
        const net = optionNetPrice(opt);
        if (opt.appliesCommitmentDiscount) {
            optionsTotal += net * (1 - commitRate);
        }
        else {
            optionsTotal += net;
        }
    }
    const perUnitAfterCommit = serverAfterCommit + optionsTotal;
    let lineMonthly = perUnitAfterCommit * line.qty;
    // For transparency: how much did commitment shave off the un-discounted full price?
    // Deliberately the LIST price: the custom discount is one of the discounts
    // this figure is the "before" of, so folding it in here would hide it from
    // the discount total the quote reports.
    const baseMonthlyBeforeDiscounts = (line.basePriceMonthly + line.options.reduce((s, o) => s + o.monthlyPrice, 0)) * line.qty;
    const commitmentDiscountAmount = round2(baseMonthlyBeforeDiscounts - lineMonthly);
    // Partner discount — applied AFTER commitment, multiplicatively, only if eligible
    let partnerDiscountAmount = 0;
    if (isPartnerDiscountEligible(line) && partnerTier !== 'none') {
        const { dealReg, profitInc } = getPartnerRates(partnerTier);
        const before = lineMonthly;
        lineMonthly = lineMonthly * (1 - dealReg) * (1 - profitInc);
        partnerDiscountAmount = round2(before - lineMonthly);
    }
    // Setup fees only apply when there's no commitment
    const setupFee = line.commitMonths === 0
        ? line.basePriceSetup * line.qty
        : 0;
    return {
        lineId: line.id,
        baseMonthlyBeforeDiscounts: round2(baseMonthlyBeforeDiscounts),
        commitmentDiscountAmount,
        partnerDiscountAmount,
        lineMonthlyAfterDiscounts: round2(lineMonthly),
        setupFee: round2(setupFee),
    };
}
/**
 * Compute the full set of totals for a quote.
 *
 * Order of operations:
 *   1. Run each line through computeLine (commitment, qty, partner discount).
 *   2. Sum line totals → subtotalMonthly.
 *   3. Apply the whole-quote special discount (%) → subtotalAfterSpecialDiscount.
 *   4. Apply support surcharge to subtotalAfterSpecialDiscount (NOT partner-discounted).
 *   5. Add support to the reduced subtotal → monthlyWithSupport.
 *   6. Compute provincial taxes on monthlyWithSupport (zeroed if tax-exempt).
 *   7. Add taxes → monthlyAllIn.
 *   8. Sum setup fees across lines.
 */
/**
 * Effective tax exemption for a quote. Exempt when the client is flagged
 * tax-exempt OR the market is WORLD (international, USD, no provincial tax).
 * Market defaults to 'CA', so for every existing CA quote this returns exactly
 * `quote.client.taxExempt` — CA behavior is unchanged.
 */
export function isTaxExempt(quote) {
    const market = quote.market ?? 'CA';
    return quote.client.taxExempt || market === 'WORLD';
}
export function computeQuoteTotals(quote) {
    // Professional Services lines are ONE-TIME fees: kept out of the monthly
    // cascade (and out of the line-item body) and surfaced separately at the
    // bottom of the quote. They never receive commitment or partner discounts.
    const isProfServices = (l) => l.isCustomLine && l.isProfessionalServices === true;
    const billableLines = quote.lines.filter((l) => !isProfServices(l));
    // A quote is "PS-only" when it has no monthly (billable) lines — every line is a
    // one-time Professional Services fee. The special discount attaches to the
    // monthly subtotal when one exists, and falls through to the PS fee otherwise.
    const hasMonthly = billableLines.length > 0;
    const lines = billableLines.map((l) => computeLine(l, quote.partnerTier));
    const subtotalMonthly = round2(lines.reduce((sum, l) => sum + l.lineMonthlyAfterDiscounts, 0));
    // Whole-quote special/exceptional discount. Entered as a percentage; clamped to
    // [0, 100] so a stray negative or >100 value can never invert or over-reduce the
    // quote. Applied to subtotalMonthly BEFORE support + taxes (both computed on the
    // reduced amount). pct 0 (or absent) => amount 0 => cascade unchanged. For a
    // PS-only quote subtotalMonthly is 0, so this is naturally 0 and the discount is
    // applied to Professional Services instead (below).
    const specialDiscountPct = Math.min(100, Math.max(0, quote.specialDiscountPct ?? 0));
    const specialDiscountRate = specialDiscountPct / 100;
    const specialDiscountAmount = round2(subtotalMonthly * specialDiscountRate);
    const subtotalAfterSpecialDiscount = round2(subtotalMonthly - specialDiscountAmount);
    const supportSurcharge = computeSupportSurcharge(subtotalAfterSpecialDiscount, quote.supportTier, quote.market ?? 'CA');
    const monthlyWithSupport = round2(subtotalAfterSpecialDiscount + supportSurcharge);
    const taxes = computeTaxes(monthlyWithSupport, quote.client.province, isTaxExempt(quote));
    const monthlyAllIn = round2(monthlyWithSupport + taxes.total);
    const setupFeesTotal = round2(lines.reduce((sum, l) => sum + l.setupFee, 0));
    // One-time Professional Services fee: the raw entered price × qty across PS
    // lines. It never gets the commitment or partner discount. The whole-quote
    // special discount reaches it ONLY on a PS-only quote (no monthly lines) — a
    // quote that also has monthly products applies the discount to the monthly and
    // bills PS in full. professionalServicesTotal is the NET (post-discount) amount
    // every downstream total / tax twin anchors on.
    const professionalServicesGross = round2(quote.lines
        .filter(isProfServices)
        .reduce((sum, l) => sum + l.basePriceMonthly * l.qty, 0));
    const professionalServicesDiscountAmount = hasMonthly
        ? 0
        : round2(professionalServicesGross * specialDiscountRate);
    const professionalServicesTotal = round2(professionalServicesGross - professionalServicesDiscountAmount);
    return {
        lines,
        subtotalMonthly,
        specialDiscountPct,
        specialDiscountAmount,
        subtotalAfterSpecialDiscount,
        supportSurcharge: round2(supportSurcharge),
        monthlyWithSupport,
        taxes,
        monthlyAllIn,
        setupFeesTotal,
        professionalServicesGross,
        professionalServicesDiscountAmount,
        professionalServicesTotal,
    };
}
